/**
 * Subscription utility functions for Acuity stylists
 * 
 * These functions provide a consistent way to check subscription status
 * across the entire application, ensuring logic is centralized and maintainable.
 */

/**
 * Check if a user is an Acuity stylist
 * @param {Object} user - User object from Sharetribe
 * @returns {boolean} True if user is an Acuity stylist
 */
export const isAcuityStylist = (user) => {
  if (!user?.attributes?.profile?.publicData) {
    return false;
  }
  return user.attributes.profile.publicData.Acuity_yes_no === 'Yes';
};

/**
 * Check if a user has an active subscription
 * @param {Object} user - User object from Sharetribe
 * @returns {boolean} True if user has active subscription
 */
export const hasActiveSubscription = (user) => {
  if (!user?.attributes?.profile?.publicData) {
    return false;
  }
  return user.attributes.profile.publicData.acuityStylistSubscribed === true;
};

/**
 * Check if a user is an unsubscribed Acuity stylist
 * This is the main check used throughout the app to determine if
 * subscription alert should be shown or if user should be filtered out
 * 
 * @param {Object} user - User object from Sharetribe
 * @returns {boolean} True if user is Acuity stylist without active subscription
 */
export const isUnSubscribedAcuityStylist = (user) => {
  if (!isAcuityStylist(user)) {
    return false;
  }
  return !hasActiveSubscription(user);
};

/**
 * Get subscription status for a user
 * @param {Object} user - User object from Sharetribe
 * @returns {'active' | 'inactive' | 'not_applicable'}
 */
export const getSubscriptionStatus = (user) => {
  if (!isAcuityStylist(user)) {
    return 'not_applicable';
  }
  return hasActiveSubscription(user) ? 'active' : 'inactive';
};

